import { useEffect, useState } from 'react';
import type { AnchorCategory } from '@/components/publish-flow/types';
import { v1 } from '@/infrastructure/api/v1/client';
import { listAll } from '@/infrastructure/api/v1/lists';
import { assetInstanceToLegacy } from '@/infrastructure/api/v1/mappers/assetInstances';

/**
 * The category of the scene's most recently placed anchor asset, or place_anywhere when it has none
 * or the read fails. Read in creation order and the last one taken, as v1 has no newest-first order
 * for asset instances (the generic route ordered by the bigint id, descending).
 */
export async function latestAnchorCategory(sceneInstanceId: string): Promise<AnchorCategory> {
  const { data } = await listAll(page => v1.GET('/api/v1/asset-instances', {
    params: { query: { sceneUniqueId: [sceneInstanceId], category: ['immersal', 'image_anchor', 'geospatial_anchor'], sort: 'createdAt', ...page } },
  }));
  const latest = data?.length ? assetInstanceToLegacy(data[data.length - 1]) : null;
  return (latest?.asset_instance_category as AnchorCategory | undefined) ?? 'place_anywhere';
}

// Derives a scene's current anchor type from its anchor asset_instance rather
// than the denormalized scene_instances field, so a scan added or removed
// mid-session is reflected without an editor reload. Re-queries whenever the id
// changes (pass a nulled id while a dialog is closed to refetch on each open).
export function useSceneAnchorCategory(sceneInstanceId: string | null | undefined): {
  anchorCategory: AnchorCategory | undefined;
  ready: boolean;
} {
  const [state, setState] = useState<{ anchorCategory: AnchorCategory | undefined; ready: boolean }>({
    anchorCategory: undefined,
    ready: !sceneInstanceId,
  });

  // A new id drops the old category; with no id there is nothing to read, so it is ready at once
  const [prevSceneInstanceId, setPrevSceneInstanceId] = useState(sceneInstanceId);
  if (prevSceneInstanceId !== sceneInstanceId) {
    setPrevSceneInstanceId(sceneInstanceId);
    setState({ anchorCategory: undefined, ready: !sceneInstanceId });
  }

  useEffect(() => {
    if (!sceneInstanceId) return;
    let cancelled = false;
    (async () => {
      try {
        const anchorCategory = await latestAnchorCategory(sceneInstanceId);
        if (cancelled) return;
        setState({ anchorCategory, ready: true });
      } catch (err) {
        if (cancelled) return;
        console.error('useSceneAnchorCategory failed:', err);
        setState({ anchorCategory: undefined, ready: true });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sceneInstanceId]);

  return state;
}
